import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useParams, Link } from "react-router-dom";
import remarkGfm from "remark-gfm";
import SEO from "../components/common/SEO";

const PAGE_TITLES = {
  privacy: "Privacy Policy - Wise Sight",
  terms: "Terms of Service - Wise Sight",
  support: "Support - Wise Sight",
};

const PAGE_FILES = {
  privacy: "privacy-policy.md",
  terms: "terms-of-service.md",
  support: "support.md",
};

export default function WiseSightPage() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { page } = useParams();

  useEffect(() => {
    async function fetchContent() {
      try {
        const filename = PAGE_FILES[page];
        if (!filename) {
          throw new Error("Page not found");
        }

        const response = await fetch(`/wise-sight/${filename}`);
        if (!response.ok) {
          throw new Error("Page not found");
        }
        const text = await response.text();
        setContent(text);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchContent();
  }, [page]);

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-900 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Link to="/" className="text-gray-600 hover:text-gray-900 underline">
            Return home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO title={PAGE_TITLES[page]} description={`Wise Sight - ${page}`} />
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl bg-white rounded-lg shadow-sm p-8 md:p-12">
          {/* Navigation breadcrumb */}
          <nav className="mb-8 text-sm text-gray-500">
            <Link to="/" className="hover:text-gray-900">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-900">Wise Sight</span>
            <span className="mx-2">/</span>
            <span className="text-gray-900 capitalize">{page}</span>
          </nav>

          {/* Markdown content */}
          <article className="prose prose-lg prose-gray max-w-none">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                // Style links to match the design
                a: ({ node, ...props }) => (
                  <a
                    {...props}
                    className="text-blue-600 hover:text-blue-800 underline"
                    target={props.href?.startsWith("http") ? "_blank" : undefined}
                    rel={props.href?.startsWith("http") ? "noopener noreferrer" : undefined}
                  />
                ),
              }}
            >
              {content}
            </ReactMarkdown>
          </article>

          {/* Footer navigation */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex flex-wrap gap-4 justify-center text-sm">
              {page !== "privacy" && (
                <Link
                  to="/wise-sight/privacy"
                  className="text-gray-600 hover:text-gray-900 underline"
                >
                  Privacy Policy
                </Link>
              )}
              {page !== "terms" && (
                <Link
                  to="/wise-sight/terms"
                  className="text-gray-600 hover:text-gray-900 underline"
                >
                  Terms of Service
                </Link>
              )}
              {page !== "support" && (
                <Link
                  to="/wise-sight/support"
                  className="text-gray-600 hover:text-gray-900 underline"
                >
                  Support
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
